import { useCallback, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { useToast } from '@/hooks/ui';
import { createLogger } from '@/lib/structuredLogger';

export interface AdminCompany {
  id: string;
  name: string;
  display_name: string;
  company_code?: string | null;
  industry?: string | null;
  seat_limit: number;
  created_at: string;
}

export interface AdminUserProfile {
  id: string;
  email: string;
  department: string | null;
  company_code: string | null;
  age: number | null;
  created_at: string;
  last_activity_at: string | null;
  total_points: number;
  deleted_at: string | null;
}

export type AdminInvitationCode = Pick<
  Tables<'invitation_codes'>,
  'id' | 'code' | 'company_id' | 'invited_email' | 'expires_at' | 'used_by' | 'created_at' | 'notes'
> & {
  company: Pick<Tables<'companies'>, 'id' | 'name' | 'display_name' | 'seat_limit'> | null;
  used_by_profile: Pick<Tables<'profiles'>, 'email'> | null;
};

type AdminLoadingKey = 'companies' | 'users' | 'invitationCodes';

interface AdminLoadingState {
  companies: boolean;
  users: boolean;
  invitationCodes: boolean;
}

const INITIAL_LOADING_STATE: AdminLoadingState = {
  companies: true,
  users: true,
  invitationCodes: true,
};

interface CreateCompanyPayload {
  name: string;
  displayName?: string;
  companyCode?: string;
  seatLimit: number;
}

interface GenerateCodesPayload {
  companyId: string;
  count: number;
  notes?: string;
}

interface GenerateEmailInvitesPayload {
  companyId: string;
  emails: string[];
  notes?: string;
  expiresAt?: string | null;
}

export const useAdminData = () => {
  const { toast } = useToast();
  const logger = useMemo(() => createLogger('AdminData'), []);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [users, setUsers] = useState<AdminUserProfile[]>([]);
  const [invitationCodes, setInvitationCodes] = useState<AdminInvitationCode[]>([]);
  const [loading, setLoading] = useState<AdminLoadingState>(INITIAL_LOADING_STATE);

  const [loadErrors, setLoadErrors] = useState<Record<AdminLoadingKey, boolean>>({
    companies: false,
    users: false,
    invitationCodes: false,
  });

  const setLoadingState = useCallback((key: AdminLoadingKey, value: boolean) => {
    setLoading((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const fetchCompanies = useCallback(async () => {
    setLoadingState('companies', true);
    setLoadErrors((previous) => ({ ...previous, companies: false }));
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setCompanies((data ?? []) as AdminCompany[]);
    } catch (error) {
      logger.error('Admin list unavailable', error);
      setLoadErrors((previous) => ({ ...previous, companies: true }));
    } finally {
      setLoadingState('companies', false);
    }
  }, [setLoadingState, logger]);

  const fetchUsers = useCallback(async () => {
    setLoadingState('users', true);
    setLoadErrors((previous) => ({ ...previous, users: false }));
    try {
      const { data, error } = await supabase.rpc('admin_get_all_profiles');

      if (error) throw error;

      setUsers((data ?? []) as AdminUserProfile[]);
    } catch (error) {
      logger.error('Admin list unavailable', error);
      setLoadErrors((previous) => ({ ...previous, users: true }));
    } finally {
      setLoadingState('users', false);
    }
  }, [setLoadingState, logger]);

  const fetchInvitationCodes = useCallback(async () => {
    setLoadingState('invitationCodes', true);
    setLoadErrors((previous) => ({ ...previous, invitationCodes: false }));
    try {
      const { data, error } = await supabase
        .from('invitation_codes')
        .select(
          `
          id, code, company_id, invited_email, expires_at, used_by, created_at, notes,
          company:companies(id, name, display_name, seat_limit),
          used_by_profile:profiles!invitation_codes_used_by_fkey(email)
        `,
        )
        .order('created_at', { ascending: false });

      if (error) throw error;

      setInvitationCodes(data ?? []);
    } catch (error) {
      logger.error('Admin list unavailable', error);
      setLoadErrors((previous) => ({ ...previous, invitationCodes: true }));
    } finally {
      setLoadingState('invitationCodes', false);
    }
  }, [setLoadingState, logger]);

  const createCompany = useCallback(
    async ({ name, displayName, companyCode, seatLimit }: CreateCompanyPayload) => {
      const normalizedCompanyCode = companyCode?.trim().toUpperCase();
      const { error } = await supabase.rpc('admin_create_pilot_company', {
        p_name: name,
        p_display_name: displayName ?? name,
        p_company_code: normalizedCompanyCode ?? '',
        p_seat_limit: seatLimit,
      });

      if (error) {
        logger.error('Error creating company:', error);
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Success',
        description: 'Company created successfully!',
      });

      await fetchCompanies();
      return true;
    },
    [toast, fetchCompanies, logger],
  );

  const generateInvitationCodes = useCallback(
    async ({ companyId, count, notes }: GenerateCodesPayload) => {
      const { data, error } = await supabase.rpc('admin_generate_invitation_codes', {
        p_company_id: companyId,
        p_count: count,
        p_notes: notes || undefined,
      });

      if (error) {
        logger.error('Error generating invitation codes:', error);
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Success',
        description: `Generated ${count} invitation code(s)!`,
      });

      await fetchInvitationCodes();
      return Boolean(data);
    },
    [toast, fetchInvitationCodes, logger],
  );

  const generateEmailInvites = useCallback(
    async ({ companyId, emails, notes, expiresAt }: GenerateEmailInvitesPayload) => {
      const { data, error } = await supabase.rpc('owner_generate_email_invites', {
        p_company_id: companyId,
        p_emails: emails,
        p_notes: notes || undefined,
        p_expires_at: expiresAt || undefined,
      });

      if (error) {
        logger.error('Error generating email invitation codes:', error);
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Success',
        description: `Generated ${data?.length ?? emails.length} email invite(s)!`,
      });

      await fetchInvitationCodes();
      return true;
    },
    [toast, fetchInvitationCodes, logger],
  );

  const deleteInvitationCodes = useCallback(
    async (codeIds: string[]) => {
      const { data, error } = await supabase.rpc('admin_delete_invitation_codes', {
        p_code_ids: codeIds,
      });

      if (error) {
        logger.error('Error deleting invitation codes:', error);
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Success',
        description: `Deleted ${data} invitation code(s)!`,
      });

      await fetchInvitationCodes();
      return true;
    },
    [toast, fetchInvitationCodes, logger],
  );

  const archiveUserProfile = useCallback(
    async (userId: string) => {
      const { error } = await supabase
        .from('profiles')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', userId);

      if (error) {
        logger.error('Error archiving user profile:', error);
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Success',
        description:
          'Profile archived. Sign-in, company membership, and personal data remain active.',
      });

      await fetchUsers();
      return true;
    },
    [toast, fetchUsers, logger],
  );

  const restoreUserProfile = useCallback(
    async (userId: string) => {
      const { error } = await supabase
        .from('profiles')
        .update({ deleted_at: null })
        .eq('id', userId);

      if (error) {
        logger.error('Error restoring archived profile:', error);
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Success',
        description: 'Profile archive cleared.',
      });

      await fetchUsers();
      return true;
    },
    [toast, fetchUsers, logger],
  );

  const offboardCompanyMember = useCallback(
    async (userId: string) => {
      const { data, error } = await supabase.rpc('admin_offboard_company_member', {
        p_user_id: userId,
      });

      if (error || !data) {
        logger.error(
          'Error offboarding company member',
          error ?? new Error('No active membership'),
        );
        toast({
          title: 'Error',
          description: error?.message ?? 'No active company membership was found.',
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Success',
        description: 'Company seat released. The personal account and health data were preserved.',
      });

      await Promise.all([fetchUsers(), fetchCompanies()]);
      return true;
    },
    [fetchCompanies, fetchUsers, logger, toast],
  );

  return {
    companies,
    users,
    invitationCodes,
    loading,
    loadErrors,
    fetchCompanies,
    fetchUsers,
    fetchInvitationCodes,
    createCompany,
    generateInvitationCodes,
    generateEmailInvites,
    deleteInvitationCodes,
    archiveUserProfile,
    restoreUserProfile,
    offboardCompanyMember,
  };
};
